import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import _ from 'lodash';
import { useEditMode } from '../components/EditMode/EditModeContext';
import {
  Paper, Typography,
  Grid, CircularProgress, Alert, Box,
  Button,
  IconButton, Dialog, DialogContent,
  Switch, FormControlLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { getNodeSize, getNodeColor, getNodeStrokeColor, getNodeFontSize, getNodeLabel, getNodeIcon, isExecutiveRole } from '../utils/ownershipNetworkHelpers';
import Legend from '../components/Legend';
import { colors, typography } from "../styles/globals.js"
import NodeDetails from '../components/NodeDetails';
import { isInactive as isInactiveShared } from '../constants/entityStatusConstants';
import { GraphEditToolbar } from '../components/EditMode/GraphEditControls';

export const isInactive = (d, input_status = null) => {
  return isInactiveShared(d, input_status);
};

const styles = {
  buttonStyle: {
    position: 'absolute',
    zIndex: 1000,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(4px)',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    }
  },
  expandedPaperStyle: {
    height: '100%',
    overflow: 'hidden',
    border: 'none',
    borderRadius: '12px',
    position: 'relative',
    backgroundColor: '#f5f5f5'
  },
  normalPaperStyle: {
    height: 520,
    borderRadius: 4,
    overflow: 'hidden',
    position: 'relative'
  },
  dialogStyle: {
    '& .MuiDialog-paper': {
      backgroundColor: '#f5f5f5',
      margin: 0,
      width: 'min(1310px, 90vw)',
      height: 'min(750px, 85vh)',
      maxWidth: 'none',
      maxHeight: 'none',
      borderRadius: '12px'
    }
  },
  headerBoxStyle: {
    py: 2,
    display: "flex",
    flex: 1,
    justifyContent: "space-between",
    alignItems: "center"
  },
  chipStyle: {
    backgroundColor: '#4CAF50',
    color: colors.white,
    fontWeight: '500',
    borderRadius: '15px',
    padding: '6px 12px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    '& .MuiChip-icon': {
      color: colors.white,
    }
  },
  loadingBoxStyle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px"
  }
};

const HIGH_CONFIDENCE_THRESHOLD = 85;

// Helper function to check if a business node has low confidence connections
const isLowConfidenceBusiness = (node, edges, entity) => {
  if (node.id === entity?.business_entity_id) return false;

  // Find all edges connected to this specific node
  const nodeEdges = edges.filter(edge =>
    (edge.source.id || edge.source) === node.id ||
    (edge.target.id || edge.target) === node.id
  );

  // If we can't find edges (safety check), don't hide it
  if (nodeEdges.length === 0) return false;

  // ONLY hide if EVERY single connection to this business is low confidence.
  // Since Western has a solid link to the address, it will stay.
  return nodeEdges.every(edge => (edge.confidence ?? 100) < HIGH_CONFIDENCE_THRESHOLD);
};

const OwnershipNetworkView = ({ graphData, entity, title, isExpanded = false, onCloseExpanded, onUpdateGraph, ubos, onCombinedUpdate }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const zoomBehaviorRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // eslint-disable-line no-unused-vars
  const [selectedNode, setSelectedNode] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedView, setExpandedView] = useState(false);
  const [showLowConfidenceBusinesses, setShowLowConfidenceBusinesses] = useState(true);
  // In edit mode, default to the customer-visible BFS-filtered view so admins
  // edit exactly what customers see. Toggle on to surface out-of-view orphans
  // and distant candidates for cleanup.
  const [showAllCandidates, setShowAllCandidates] = useState(false);
  const { isEditMode } = useEditMode();

  // ---- Improved BFS Filtering For Depth 3 ----

  const filteredGraphData = useMemo(() => {
    if (!graphData || !entity?.business_entity_id) return { nodes: [], edges: [] };

    const rootId = String(entity.business_entity_id);
    const MAX_HOPS = 2;

    // 1. Make an adjacency list for undirected BFS
    const adj = new Map();
    for (const node of graphData.nodes) {
      adj.set(String(node.id), []);
    }
    for (const edge of graphData.edges) {
      // Normalize to string ids
      const s = String(edge.source.id || edge.source);
      const t = String(edge.target.id || edge.target);
      if (adj.has(s) && !adj.get(s).includes(t)) adj.get(s).push(t);
      if (adj.has(t) && !adj.get(t).includes(s)) adj.get(t).push(s);
    }

    // Proper BFS from rootId for MAX_HOPS, collecting all visited nodes with distance
    const distances = { [rootId]: 0 };
    const visited = new Set([rootId]);
    const queue = [[rootId, 0]];
    while (queue.length > 0) {
      const [currentId, hop] = queue.shift();
      if (hop >= MAX_HOPS) continue; // Do not traverse past max hops
      const neighbors = adj.get(currentId) || [];
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          distances[neighbor] = hop + 1;
          visited.add(neighbor);
          queue.push([neighbor, hop + 1]);
        }
      }
    }

    // 2. Filter Nodes based on Distance + Confidence Rules
    const filteredNodes = _.filter(graphData.nodes, node => {
      const nodeId = String(node.id);
      const dist = distances[nodeId];

      // Outside edit mode (and in edit mode by default), hide anything outside
      // MAX_HOPS of the root — including floaters from past deletions. Admins
      // can flip "Show all candidates" to surface orphans for cleanup.
      if (!(isEditMode && showAllCandidates) && (dist === undefined || dist > MAX_HOPS)) return false;

      const isPrimary = nodeId === rootId;

      // Filter out low confidence addresses (adjust type casing if needed)
      const isLowConfAddress = (node.type?.toLowerCase() === 'address') && (node.data?.confidence <= 50);
      if (isLowConfAddress) return false;

      // Filter out low confidence businesses if toggle is off
      if (!showLowConfidenceBusinesses && !isPrimary && node.type === 'COMPANY') {
        if (isLowConfidenceBusiness(node, graphData.edges, entity)) return false;
      }
      return true;
    });

    // Only keep edges between the surviving nodes
    const nodeIds = new Set(_.map(filteredNodes, n => String(n.id)));
    const filteredEdges = _.filter(graphData.edges, edge => {
      const s = String(edge.source.id || edge.source);
      const t = String(edge.target.id || edge.target);
      return nodeIds.has(s) && nodeIds.has(t);
    });

    // Deep clone to prevent D3 from mutating React state
    // D3 force simulation converts source/target strings to object references in-place
    return {
      _version: Date.now(), // Force new reference on every recalculation
      nodes: filteredNodes.map(n => ({ ...n, data: { ...n.data } })),
      edges: filteredEdges.map(e => ({
        ...e,
        source: String(e.source?.id || e.source),
        target: String(e.target?.id || e.target),
      })),
    };
  }, [graphData, showLowConfidenceBusinesses, showAllCandidates, entity, isEditMode]);
  // D3 visualization
  useEffect(() => {
    if (!filteredGraphData || !svgRef.current || !containerRef.current) return;

    setLoading(false);

    // Clear previous visualization
    d3.select(svgRef.current).selectAll("*").remove();

    const width = containerRef.current.clientWidth;
    const height = isExpanded ? Math.min(750, window.innerHeight * 0.85) : 1000;

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height]);

    // Add this section to create the grid pattern:
    const defs = svg.append('defs');

    // Create grid pattern
    const gridPattern = defs.append('pattern')
      .attr('id', 'grid')
      .attr('width', 20)
      .attr('height', 20)
      .attr('patternUnits', 'userSpaceOnUse');

    // Add grey background to the pattern
    gridPattern.append('rect')
      .attr('width', 20)
      .attr('height', 20)
      .attr('fill', '#f8fafc');

    // Add 4 dots per grid pattern
    const dotPositions = [
      { x: 5, y: 5 },
      { x: 15, y: 5 },
      { x: 5, y: 15 },
      { x: 15, y: 15 }
    ];

    dotPositions.forEach(pos => {
      gridPattern.append('circle')
        .attr('cx', pos.x)
        .attr('cy', pos.y)
        .attr('r', 1)
        .attr('fill', '#a5b4fc')
        .attr('opacity', 0.6);
    });

    // Create zoom container upfront
    const zoomContainer = svg.append('g').attr('class', 'zoom-container');

    // Add background rectangle with pattern INSIDE zoom container
    // Make it extra large (20x viewport size) to prevent white background showing
    // during dynamic resizing, zooming, or panning
    zoomContainer.append('rect')
      .attr('x', -width * 10)
      .attr('y', -height * 10)
      .attr('width', width * 20)
      .attr('height', height * 20)
      .attr('fill', 'url(#grid)');

    // Create simulation with more aggressive alphaDecay (faster cooling)
    const simulation = d3.forceSimulation(filteredGraphData.nodes)
      .force('link', d3.forceLink(filteredGraphData.edges)
        .id(d => d.id)
        .distance(d => {
          const sourceType = d.source.type || (typeof d.source === 'object' ? d.source.type : null);
          const targetType = d.target.type || (typeof d.target === 'object' ? d.target.type : null);
          // console.log('sourceType', sourceType, 'targetType', targetType)
          if ((sourceType === 'COMPANY' && targetType === 'COMPANY') ||
            (sourceType === 'COMPANY' && targetType === 'COMPANY')) {
            // console.log('here 1')
            return 300;
          }
          if ((sourceType === 'COMPANY' && targetType === 'PERSON') ||
            (sourceType === 'PERSON' && targetType === 'COMPANY')) {
            // console.log('here 2')
            return 500
          }
          if ((sourceType === 'COMPANY' && targetType === 'ADDRESS') ||
            (sourceType === 'ADDRESS' && targetType === 'COMPANY')) {
            // console.log('here 3')
            return 400;
          }
          if ((sourceType === 'PERSON' && targetType === 'PERSON') ||
            (sourceType === 'PERSON' && targetType === 'PERSON')) {
            // console.log('here 4')
            return 300;
          }
          if ((sourceType === 'PERSON' && targetType === 'ADDRESS') ||
            (sourceType === 'ADDRESS' && targetType === 'PERSON')) {
            // console.log('here 5')
            return 350;
          }
          if ((sourceType === 'ADDRESS' && targetType === 'ADDRESS') ||
            (sourceType === 'ADDRESS' && targetType === 'ADDRESS')) {
            // console.log('here 6')
            return 500;
          }
          return 220; // fallback
        }))
      .force('charge', d3.forceManyBody().strength(-500))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collide', d3.forceCollide().radius(d => getNodeSize(d, entity) + 30))
      .force('x', d3.forceX(width / 2).strength(0.03))
      .force('y', d3.forceY(height / 2).strength(0.03))
      .alphaDecay(0.05); // Increased from 0.01 to 0.05

    // Create a group for edges
    const link = zoomContainer.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(filteredGraphData.edges)
      .enter()
      .append('line')
      .attr('stroke', d => {
        const confidence = d.confidence ?? 100;
        return confidence < HIGH_CONFIDENCE_THRESHOLD ? '#B8C5F2' : '#B2B8CF';  // Lighter color for low confidence edges
      })
      .attr('stroke-opacity', d => {
        const confidence = d.confidence ?? 100;
        return confidence < HIGH_CONFIDENCE_THRESHOLD ? 0.8 : 1;
      })
      .attr('stroke-width', d => d.weight ? d.weight * 1.3 : 2)
      .attr('stroke-dasharray', d => {
        const confidence = d.confidence ?? 100;
        if (confidence < HIGH_CONFIDENCE_THRESHOLD) {
          return '12,6';
        }
        return '';
      })
      .style('transition', 'all 0.3s ease');

    // Deduplicate edge labels
    const edgesByPair = {};
    filteredGraphData.edges.forEach(edge => {
      const sourceId = edge.source.id || edge.source;
      const targetId = edge.target.id || edge.target;
      // Create a unique key for the edge pair (sort to handle both directions)
      const pairKey = [sourceId, targetId].sort().join('-');
      if (!edgesByPair[pairKey]) {
        edgesByPair[pairKey] = { labels: [], edge: edge };
      }
      edgesByPair[pairKey].labels.push(edge.label);
    });

    // Convert to array with combined labels
    const uniqueEdges = _.map(edgesByPair, (item) => {
      return {
        ...item.edge,
        combinedLabel: [...new Set(item.labels)].join(', ')
      };
    });

    // Create edge labels with conditional styling, with pointer events off for performance
    const edgeLabelGroup = zoomContainer.append('g')
      .attr('class', 'edge-labels')
      .style('pointer-events', 'none')
      .selectAll('g')
      .data(uniqueEdges)
      .enter()
      .append('g');

    edgeLabelGroup.append('rect')
      .attr('fill', d => {
        const confidence = d.confidence ?? 100;
        const isExecutive = isExecutiveRole(d.combinedLabel);
        if (isInactive(d)) {
          return 'rgba(255, 255, 255, 0.8)';
        }
        return (confidence < HIGH_CONFIDENCE_THRESHOLD && !isExecutive) ? 'rgba(255, 255, 255, 0.9)' : 'transparent';
      })
      .attr('stroke', d => {
        const confidence = d.confidence ?? 100;
        const isExecutive = isExecutiveRole(d.combinedLabel);
        if (isInactive(d)) {
          return '#F3F4F6';
        }
        return (confidence < HIGH_CONFIDENCE_THRESHOLD && !isExecutive) ? '#ddd' : 'transparent';
      })
      .attr('stroke-width', d => {
        const confidence = d.confidence ?? 100;
        const isExecutive = isExecutiveRole(d.combinedLabel);
        return (confidence < HIGH_CONFIDENCE_THRESHOLD && !isExecutive) ? 1 : 0;
      })
      .attr('rx', 3)
      .attr('ry', 3);

    // Function to filter unwanted text from labels
    const filterLabelText = (text) => {
      // Split comma-separated roles
      const roles = text.split(',').map(role => role.trim());
      // Convert "REGISTERED AGENT" to "AGENT"
      const normalizedRoles = roles.map(role =>
        role.toUpperCase() === 'REGISTERED AGENT' ? 'AGENT' : role
      );
      // Remove duplicates (case-insensitive)
      const uniqueRoles = [];
      const seenRoles = new Set();

      for (const role of normalizedRoles) {
        const upperRole = role.toUpperCase();
        if (!seenRoles.has(upperRole)) {
          seenRoles.add(upperRole);
          uniqueRoles.push(role);
        }
      }
      // Remove OWNER unless it's the only string
      let filteredRoles = uniqueRoles;
      if (filteredRoles.length > 1) {
        filteredRoles = filteredRoles.filter(role => role.toUpperCase() !== 'OWNER');
      }
      // Sort roles with AGENT and ORGANIZER at the end
      const sortedRoles = filteredRoles.sort((a, b) => {
        const aUpper = a.toUpperCase();
        const bUpper = b.toUpperCase();
        if ((aUpper === 'AGENT' || aUpper === 'ORGANIZER') &&
          (bUpper === 'AGENT' || bUpper === 'ORGANIZER')) {
          return 0;
        }
        if (aUpper === 'AGENT' || aUpper === 'ORGANIZER') {
          return 1;
        }
        if (bUpper === 'AGENT' || bUpper === 'ORGANIZER') {
          return -1;
        }
        return aUpper.localeCompare(bUpper);
      });

      return sortedRoles.join(', ');
    };

    // Add the text labels with color coding (still respond to hover, so pointer-events only off on group, not on text)
    const edgeLabels = edgeLabelGroup.append('text')
      .attr('font-size', '11px')
      .attr('font-weight', '600')
      .attr('font-family', 'system-ui, -apple-system, sans-serif')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', d => {
        const confidence = d.confidence ?? 100;
        const isExecutive = isExecutiveRole(d.combinedLabel);
        if (isInactive(d)) {
          return '#000000';
        }
        if (isExecutive) {
          return '#0085CD';
        }
        if (confidence >= HIGH_CONFIDENCE_THRESHOLD) {
          return '#0029CD';
        }
        return '#333';
      })
      .text(d => {
        const filteredText = filterLabelText(d.combinedLabel);
        const label = filteredText.toUpperCase().replace(/_/g, ' ');
        // Trim label if longer than 35 characters
        if (label.length > 35) {
          return label.substring(0, 32) + '...';
        }
        return label;
      })
      .style('cursor', 'default')
      .style('user-select', 'none')
      .style('text-shadow', d => {
        const confidence = d.confidence ?? 100;
        const isExecutive = isExecutiveRole(d.combinedLabel);
        if (isInactive(d)) {
          return '1px 1px 2px rgba(255,255,255,0.7)';
        }
        return (confidence >= HIGH_CONFIDENCE_THRESHOLD || isExecutive) ? '1px 1px 2px rgba(255,255,255,0.8)' : 'none';
      })
      .on('mouseover', function (event, d) {
        // Create or update tooltip
        const tooltip = d3.select('body').selectAll('.edge-label-tooltip')
          .data([d]);
        const tooltipEnter = tooltip.enter()
          .append('div')
          .attr('class', 'edge-label-tooltip')
          .style('position', 'absolute')
          .style('background', 'white')
          .style('color', 'black')
          .style('padding', '8px')
          .style('border-radius', '4px')
          .style('font-size', '12px')
          .style('pointer-events', 'none')
          .style('z-index', '10000')
          .style('opacity', 0)
          .style('border', '1px solid #ccc')
          .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)');
        const filteredText = filterLabelText(d.combinedLabel);
        tooltip.merge(tooltipEnter)
          .text(filteredText.toUpperCase().replace(/_/g, ' '))
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 30) + 'px')
          .transition()
          .duration(200)
          .style('opacity', 1);
      })
      .on('mousemove', function (event, d) {
        d3.select('.edge-label-tooltip')
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 30) + 'px');
      })
      .on('mouseout', function () {
        d3.select('.edge-label-tooltip')
          .transition()
          .duration(200)
          .style('opacity', 0)
          .remove();
      });

    // Size the background rectangles to fit the text (only for low confidence non-executive)
    edgeLabels.each(function (d) {
      const confidence = d.confidence ?? 100;
      const isExecutive = isExecutiveRole(d.combinedLabel);
      if (confidence < HIGH_CONFIDENCE_THRESHOLD && !isExecutive) {
        const textBBox = this.getBBox();
        const padding = 4;
        d3.select(this.parentNode).select('rect')
          .attr('x', textBBox.x - padding)
          .attr('y', textBBox.y - padding)
          .attr('width', textBBox.width + padding * 2)
          .attr('height', textBBox.height + padding * 2);
      }
    });

    // Create a group for nodes
    const node = zoomContainer.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(filteredGraphData.nodes)
      .enter()
      .append('g')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended))
      .on('click', (event, d) => {
        // Get the actual screen position of the clicked node element
        const nodeElement = event.currentTarget;
        const nodeRect = nodeElement.getBoundingClientRect();

        // Get the center of the node element
        const nodeCenterX = nodeRect.left + nodeRect.width / 2;
        const nodeCenterY = nodeRect.bottom;

        // Create a temporary anchor element positioned at the node's actual screen position
        const tempAnchor = document.createElement('div');
        tempAnchor.style.position = 'fixed';
        tempAnchor.style.left = `${nodeCenterX}px`;
        tempAnchor.style.top = `${nodeCenterY}px`;
        tempAnchor.style.width = '1px';
        tempAnchor.style.height = '1px';
        tempAnchor.style.pointerEvents = 'none';
        tempAnchor.style.zIndex = '9999';
        document.body.appendChild(tempAnchor);

        setAnchorEl(tempAnchor);
        setSelectedNode(d);
        event.stopPropagation();
      });

    // Helper for low confidence connection
    const hasLowConfidenceConnection = (nodeId, edges) => {
      return edges.some(edge => {
        const sourceId = edge.source.id || edge.source;
        const targetId = edge.target.id || edge.target;
        const confidence = edge.confidence ?? 100;
        return (sourceId === nodeId || targetId === nodeId) && confidence < HIGH_CONFIDENCE_THRESHOLD;
      });
    };

    // Add different shapes for different node types
    node.each(function (d) {
      const nodeElement = d3.select(this);
      const size = getNodeSize(d, entity);
      const color = getNodeColor(d, entity);
      const strokeColor = getNodeStrokeColor(d, entity);
      const isCurrentEntity = d.id === entity?.business_entity_id;
      const isCurrentEntityInvalid = isCurrentEntity && isInactive(d);

      // Check if this node has any low confidence connections
      const hasLowConfidence = hasLowConfidenceConnection(d.id, filteredGraphData.edges);

      // Calculate opacity based on confidence and inactive status
      let nodeOpacity = 1.0;
      if (isInactive(d)) {
        nodeOpacity = 0.6;
      }
      if (isInactive(d) && hasLowConfidence && !isCurrentEntity && d.type === 'COMPANY') {
        nodeOpacity = 0.2;
      } else if (hasLowConfidence && !isCurrentEntity && d.type === 'COMPANY') {
        nodeOpacity = 0.3;
      }

      if (d.type === 'COMPANY') {
        nodeElement.append('rect')
          .attr('width', size * 2)
          .attr('height', size * 2)
          .attr('x', -size)
          .attr('y', -size)
          .attr('rx', 6)
          .attr('fill', isInactive(d) ? '#E5E7EB' : color)
          .attr('opacity', nodeOpacity)
          .attr('stroke', isInactive(d) ? '#4B5563' : strokeColor)
          .attr('stroke-width', isInactive(d) ? 2 : (isCurrentEntity ? 2 : 1));

        if (isCurrentEntityInvalid) {
          nodeElement.select('rect')
            .attr('fill', '#FEE2E2')
            .attr('stroke', '#DC2626')
            .attr('stroke-width', 2)
        }
      } else {
        nodeElement.append('circle')
          .attr('r', size)
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('fill', isInactive(d) ? '#E5E7EB' : color)
          .attr('opacity', nodeOpacity)
          .attr('stroke', isInactive(d) ? '#6B7280' : strokeColor)
          .attr('stroke-width', 1);
      }
    });

    node.style("cursor", "pointer");
    node.selectAll("text, .emoji")
      .style("cursor", "pointer")
      .style("user-select", "none");

    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('alignment-baseline', 'central')
      .attr('font-family', 'Material Icons')
      .attr('font-size', d => getNodeFontSize(d, entity))
      .attr('fill', d => {
        return isInactive(d) ? '#6B7280' : '#ffffff';
      })
      .style('pointer-events', 'none')
      .text(d => getNodeIcon(d.type));

    // Add labels to each node with pill background
    const labelGroup = node.append('g')
      .attr('class', 'label-group');

    labelGroup.append('rect')
      .attr('rx', 8)
      .attr('ry', 8)
      .attr('fill', d => {
        return isInactive(d) ? 'rgba(255, 255, 255, 0.8)' : 'white';
      })
      .attr('opacity', '0.9');

    const labelText = labelGroup.append('text')
      .attr('dy', 50)
      .attr('text-anchor', 'middle')
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .attr('fill', d => {
        return isInactive(d) ? '#333' : '#333';
      })
      .text(d => getNodeLabel(d));
    labelText.each(function (d) {
      const textBBox = this.getBBox();
      const padding = 12;
      d3.select(this.parentNode).select('rect')
        .attr('x', textBBox.x - padding)
        .attr('y', textBBox.y - padding / 2)
        .attr('width', textBBox.width + padding * 2)
        .attr('height', textBBox.height + padding)
        .attr('rx', (textBBox.height + padding) / 2)
        .attr('ry', (textBBox.height + padding) / 2);
    });

    // "Lazy" tick for less overhead
    let tickCount = 0;
    simulation.on('tick', () => {
      tickCount++;
      // Move nodes and edges every tick (fast)
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node
        .attr('transform', d => `translate(${d.x},${d.y})`);

      // Move heavy label rendering less frequently (every 2nd tick)
      if (tickCount % 2 === 0) {
        edgeLabelGroup
          .attr('transform', d => `translate(${(d.source.x + d.target.x) / 2}, ${(d.source.y + d.target.y) / 2})`);
      }
    });

    // Drag functions
    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    // Add zoom functionality
    const zoom = d3.zoom()
      .scaleExtent([0.5, 5])
      .on('zoom', (event) => {
        zoomContainer.attr('transform', event.transform);
      });

    svg.call(zoom);
    zoomBehaviorRef.current = zoom;

    // Cleanup function
    return () => {
      simulation.stop();
    };
  }, [filteredGraphData, entity, isExpanded]);

  // Resolve latest node data from graphData (D3 nodes hold stale cloned data)
  const resolvedSelectedNode = useMemo(() => {
    if (!selectedNode || !graphData?.nodes) return selectedNode;
    const fresh = graphData.nodes.find(n => n.id === selectedNode.id);
    return fresh ? { ...selectedNode, ...fresh, data: { ...selectedNode.data, ...fresh.data } } : selectedNode;
  }, [selectedNode, graphData]);

  const onHandleCloseNodeDetails = () => {
    if (anchorEl) {
      document.body.removeChild(anchorEl);
      setAnchorEl(null);
    }
    setSelectedNode(null);
  }

  const onHandleExpandedView = () => {
    setExpandedView(prevExpanded => !prevExpanded)
  }

  // Zoom functions that zoom to center
  const handleZoomIn = () => {
    if (!svgRef.current || !containerRef.current || !zoomBehaviorRef.current) return;

    const svg = d3.select(svgRef.current);
    const width = containerRef.current.clientWidth;
    const height = isExpanded ? Math.min(750, window.innerHeight * 0.85) : 500;

    const centerX = width / 2;
    const centerY = height / 2;

    svg.transition().duration(300).call(
      zoomBehaviorRef.current.scaleBy, 1.5, [centerX, centerY]
    );
  };

  const handleZoomOut = () => {
    if (!svgRef.current || !containerRef.current || !zoomBehaviorRef.current) return;

    const svg = d3.select(svgRef.current);
    const width = containerRef.current.clientWidth;
    const height = isExpanded ? Math.min(750, window.innerHeight * 0.85) : 500;

    const centerX = width / 2;
    const centerY = height / 2;

    svg.transition().duration(300).call(
      zoomBehaviorRef.current.scaleBy, 1 / 1.5, [centerX, centerY]
    );
  };

  // Render the main graph content (SVG + buttons)
  const renderGraphContent = () => (
    <>
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
      <Legend />
      <IconButton
        onClick={isExpanded ? onCloseExpanded : onHandleExpandedView}
        sx={{ ...styles.buttonStyle, top: 12, right: 12 }}
      >
        {isExpanded ? <CloseIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
      </IconButton>
      <IconButton onClick={handleZoomOut} sx={{ ...styles.buttonStyle, bottom: 12, right: 12 }}>
        <ZoomOutIcon fontSize="small" />
      </IconButton>
      <IconButton onClick={handleZoomIn} sx={{ ...styles.buttonStyle, bottom: 60, right: 12 }}>
        <ZoomInIcon fontSize="small" />
      </IconButton>
    </>
  );

  const showAllCandidatesToggle = isEditMode ? (
    <FormControlLabel
      control={
        <Switch
          checked={showAllCandidates}
          onChange={(e) => setShowAllCandidates(e.target.checked)}
          size="small"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#4CAF50',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#4CAF50',
            },
          }}
        />
      }
      label={
        <Typography variant="body2" sx={{ fontSize: '14px', color: '#666' }}>
          Show all candidates
        </Typography>
      }
      sx={{ margin: 0 }}
    />
  ) : null;

  if (loading) {
    return (
      <Box sx={styles.loadingBoxStyle}>
        <CircularProgress />
        <Typography variant="body1" style={{ marginLeft: 16 }}>
          Loading graph data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={() => window.location.reload()}>
          <RefreshIcon fontSize="small" style={{ marginRight: 8 }} />
          Retry
        </Button>
      }>
        {error}
      </Alert>
    );
  }

  if (isExpanded) {
    return (
      <Paper ref={containerRef} sx={styles.expandedPaperStyle}>
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 60,
            zIndex: 1000,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            padding: '4px 12px 4px 4px',
            border: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          {showAllCandidatesToggle}
          <FormControlLabel
            control={
              <Switch
                checked={showLowConfidenceBusinesses}
                onChange={(e) => setShowLowConfidenceBusinesses(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#4CAF50',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#4CAF50',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '14px', color: '#666' }}>
                Show Low Confidence
              </Typography>
            }
            sx={{ margin: 0 }}
          />
        </Box>
        {onUpdateGraph && <GraphEditToolbar graphData={graphData} entity={entity} ubos={ubos} onUpdate={onUpdateGraph} onCombinedUpdate={onCombinedUpdate} />}
        {renderGraphContent()}
        <NodeDetails
          node={resolvedSelectedNode}
          anchorEl={anchorEl}
          onClose={onHandleCloseNodeDetails}
        />
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ px: 2, pb: 1, mb: 2, borderRadius: 2, border: '1px solid #D9DDF0' }}>
      <Box sx={styles.headerBoxStyle}>
        {title && (
          <Typography sx={{ fontWeight: typography.fontWeights.semibold, fontSize: typography.fontSizes.h3 }}>
            {title}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {showAllCandidatesToggle}
          <FormControlLabel
            control={
              <Switch
                checked={showLowConfidenceBusinesses}
                onChange={(e) => setShowLowConfidenceBusinesses(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#4CAF50',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#4CAF50',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '14px', color: '#666' }}>
                Show Low Confidence
              </Typography>
            }
            sx={{ margin: 0 }}
          />
        </Box>
      </Box >
      {onUpdateGraph && <GraphEditToolbar graphData={graphData} entity={entity} ubos={ubos} onUpdate={onUpdateGraph} onCombinedUpdate={onCombinedUpdate} />}
      <Box>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12}>
            <Paper ref={containerRef} sx={styles.normalPaperStyle}>
              {renderGraphContent()}
            </Paper>
            <NodeDetails
              node={selectedNode}
              anchorEl={anchorEl}
              onClose={onHandleCloseNodeDetails}
            />
          </Grid>
        </Grid >
      </Box >

      {/* Fullscreen Dialog */}
      {
        expandedView && (
          <Dialog
            open={expandedView}
            onClose={() => setExpandedView(false)}
            maxWidth={false}
            sx={styles.dialogStyle}
          >
            <DialogContent sx={{ p: 0, height: '100%', position: 'relative' }}>
              {/* Expanded view content */}
              <OwnershipNetworkView
                graphData={graphData}
                entity={entity}
                ubos={ubos}
                title={title}
                isExpanded={true}
                onCloseExpanded={onHandleExpandedView}
                onUpdateGraph={onUpdateGraph}
                onCombinedUpdate={onCombinedUpdate}
              />
            </DialogContent>
          </Dialog>
        )
      }
    </Paper >
  );
};

export default OwnershipNetworkView;